import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
type PromoSlide = {
  id: string;
  bg: string;
  accent: string;
  badge?: string;
  headline: string;
  subline: string;
  tagline: string;
  emoji: string;
};
const PROMOS: PromoSlide[] = [{
  id: '1',
  bg: 'linear-gradient(160deg, #7f1d1d 0%, #991b1b 50%, #b91c1c 100%)',
  accent: '#fbbf24',
  badge: '🔥 HOY',
  headline: 'HAMBURGUESAS\nARTESANALES',
  subline: 'JUGOSAS · GIGANTES · IRRESISTIBLES',
  tagline: 'No se comparten. Se repiten.',
  emoji: '🍔'
}, {
  id: '2',
  bg: 'linear-gradient(160deg, #1c1917 0%, #292524 50%, #44403c 100%)',
  accent: '#f59e0b',
  badge: '⭐ ESPECIAL',
  headline: 'TABLA\nPARRILLERA',
  subline: 'PARA 2 · 4 · 6 PERSONAS',
  tagline: 'La experiencia completa de La Cochera.',
  emoji: '🥩'
}, {
  id: '3',
  bg: 'linear-gradient(160deg, #0c4a6e 0%, #075985 50%, #0369a1 100%)',
  accent: '#38bdf8',
  badge: '🍹 NOCHE',
  headline: 'COCTELES\nDE AUTOR',
  subline: 'CLÁSICOS · FROZEN · SHOTS',
  tagline: 'La noche empieza aquí.',
  emoji: '🍹'
}, {
  id: '4',
  bg: 'linear-gradient(160deg, #14532d 0%, #166534 50%, #15803d 100%)',
  accent: '#86efac',
  badge: '🌿 FRESCO',
  headline: 'MOFONGOS\nARTESANALES',
  subline: 'CHICHARRÓN · LONGANIZA · CAMARONES',
  tagline: 'Tradición dominicana en cada bocado.',
  emoji: '🫙'
}];
type PromoModalProps = {
  onClose: () => void;
};
export function PromoModal({
  onClose
}: PromoModalProps) {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((idx: number, dir: number) => {
    setDirection(dir);
    setCurrent(idx);
  }, []);
  const next = useCallback(() => {
    goTo((current + 1) % PROMOS.length, 1);
  }, [current, goTo]);
  const prev = useCallback(() => {
    goTo((current - 1 + PROMOS.length) % PROMOS.length, -1);
  }, [current, goTo]);
  // Auto-advance
  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  const slide = PROMOS[current];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{
    backgroundColor: 'rgba(0,0,0,0.88)'
  }} onClick={onClose} data-id="element-182">
      {/* Card */}
      <motion.div initial={{
      scale: 0.88,
      opacity: 0,
      y: 24
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} exit={{
      scale: 0.88,
      opacity: 0,
      y: 24
    }} transition={{
      type: 'spring',
      damping: 22,
      stiffness: 280
    }} className="relative w-full max-w-[320px] rounded-3xl overflow-hidden shadow-2xl" style={{
      aspectRatio: '9/16',
      maxHeight: '80vh'
    }} onClick={e => e.stopPropagation()} data-id="element-183">
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-30 flex gap-1.5 p-3" data-id="element-184">
          {PROMOS.map((p, i) => <div key={p.id} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden" data-id="element-185">
              <motion.div className="h-full bg-white rounded-full" initial={{
            width: i < current ? '100%' : '0%'
          }} animate={{
            width: i < current ? '100%' : i === current ? '100%' : '0%'
          }} transition={i === current ? {
            duration: 4,
            ease: 'linear'
          } : {
            duration: 0.1
          }} key={`${p.id}-${current}`} data-id="element-186" />
            </div>)}
        </div>

        {/* Close button */}
        <button onClick={onClose} className="absolute top-8 right-3 z-30 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors" data-id="element-187">
          <X className="w-4 h-4" data-id="element-188" />
        </button>

        {/* Slide content */}
        <AnimatePresence custom={direction} mode="wait" data-id="element-189">
          <motion.div key={slide.id} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.35,
          ease: [0.32, 0.72, 0, 1]
        }} className="absolute inset-0 flex flex-col" style={{
          background: slide.bg
        }} data-id="element-190">
            {/* Top section */}
            <div className="relative z-10 pt-14 px-6 flex-shrink-0" data-id="element-191">
              {/* Logo */}
              <img src="https://cdn.magicpatterns.com/uploads/ewfDCj5QKNDNeQRsTcwjBG/logo_de_Junquito_Gas_(4).ai_(4).png" alt="La Cochera" className="h-10 mb-5 drop-shadow-lg" data-id="element-192" />

              {/* Badge */}
              {slide.badge && <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{
              backgroundColor: `${slide.accent}25`,
              color: slide.accent,
              border: `1px solid ${slide.accent}40`
            }} data-id="element-193">
                  {slide.badge}
                </div>}

              {/* Headline */}
              <h2 className="font-black text-white leading-none tracking-tight mb-3" style={{
              fontSize: 'clamp(2rem, 8vw, 2.5rem)',
              textShadow: '0 2px 20px rgba(0,0,0,0.4)'
            }} data-id="element-194">
                {slide.headline.split('\n').map((line, i) => <Fragment key={i} data-id="element-195">
                    {line}
                    {i < slide.headline.split('\n').length - 1 && <br data-id="element-196" />}
                  </Fragment>)}
              </h2>

              {/* Subline */}
              <p className="text-xs font-black uppercase tracking-[0.2em] mb-6" style={{
              color: slide.accent
            }} data-id="element-197">
                {slide.subline}
              </p>
            </div>

            {/* Emoji / Food visual */}
            <div className="flex-1 flex items-center justify-center relative z-10" data-id="element-198">
              <div className="text-[120px] leading-none select-none" style={{
              transform: 'scale(1.1)'
            }} data-id="element-199">
                {slide.emoji}
              </div>
            </div>

            {/* Bottom tagline */}
            <div className="relative z-10 px-6 pb-10 pt-4" data-id="element-200">
              <div className="h-px w-12 mb-4 rounded-full" style={{
              backgroundColor: slide.accent
            }} data-id="element-201" />
              <p className="text-white/80 text-sm font-semibold italic leading-snug" data-id="element-202">
                {slide.tagline}
              </p>
            </div>

            {/* Gradient overlay bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)'
          }} data-id="element-203" />
          </motion.div>
        </AnimatePresence>

        {/* Nav arrows */}
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors" data-id="element-204">
          <ChevronLeft className="w-4 h-4" data-id="element-205" />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors" data-id="element-206">
          <ChevronRight className="w-4 h-4" data-id="element-207" />
        </button>

        {/* Dot indicators */}
        <div className="absolute bottom-4 left-0 right-0 z-30 flex justify-center gap-1.5" data-id="element-208">
          {PROMOS.map((p, i) => <button key={p.id} onClick={() => goTo(i, i > current ? 1 : -1)} className="rounded-full transition-all" style={{
          width: i === current ? 20 : 6,
          height: 6,
          backgroundColor: i === current ? 'white' : 'rgba(255,255,255,0.4)'
        }} data-id="element-209" />)}
        </div>
      </motion.div>
    </motion.div>;
}